import { useEffect, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from '@frontend/i18n';
import { NakedButton, Text, TextLink } from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import { useFillingExperienceContext } from '../../../../provider';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import { ESignValue, isESignSigned, parseESignValue } from '../../signature/esign-value';
import { formatSignatureTimestamp } from '../../signature/timestamp';
import { useDynamicFormStore } from '../../store';
import type { DynamicFormField } from '../../types';
import {
  fieldErrorStyle,
  fieldLabelStyle,
  fieldsetStyle,
  signBoxStyle,
  signedContainerStyle,
  signedMetaStyle,
} from './esign-field.styles';
import { ESignModal } from './esign-modal';
import { ESignPreview } from './esign-preview';

export interface ESignFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

export const ESignField = ({ form, fieldInfo }: ESignFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { isPreviewMode } = useFillingExperienceContext();
  const fieldKey = fieldInfo.key;

  const fieldErrors = useStore(form.store, (state) => {
    const meta = state.fieldMeta[fieldKey];
    if (!meta?.isTouched) return [];
    return (meta?.errors ?? []) as string[];
  });

  const validate = ({ value }: { value: unknown }) => {
    if (isPreviewMode) return undefined;
    if (fieldInfo.required && !isESignSigned(parseESignValue(value))) return t('Signature is required');
    return undefined;
  };

  return (
    <fieldset
      css={fieldsetStyle}
      role='group'
      aria-required={!!fieldInfo.required}
      aria-labelledby={`${fieldKey}-label`}
    >
      <Text id={`${fieldKey}-label`} css={fieldLabelStyle}>
        {formatFieldLabel(fieldInfo.label, fieldInfo.required)}
      </Text>

      <form.AppField name={fieldKey} validators={{ onChange: validate }}>
        {(field) => (
          <ESignInput
            fieldKey={fieldKey}
            value={parseESignValue(field.state.value)}
            hasError={fieldErrors.length > 0}
            onChange={(value) => {
              field.handleChange(value);
              field.handleBlur();
            }}
          />
        )}
      </form.AppField>

      {fieldErrors.length > 0 && (
        <Text size='small' css={fieldErrorStyle} role='alert'>
          {fieldErrors.join(', ')}
        </Text>
      )}
    </fieldset>
  );
};

interface ESignInputProps {
  fieldKey: string;
  value: ESignValue;
  hasError: boolean;
  onChange: (value: ESignValue) => void;
}

const ESignInput = ({ fieldKey, value, hasError, onChange }: ESignInputProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const { addESignHistory } = useDynamicFormStore(['addESignHistory']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSigned = isESignSigned(value);

  // A value restored from a draft (or autofill) joins the session history so other eSign fields can reuse it.
  useEffect(() => {
    if (isSigned) addESignHistory(value);
    // Only on mount: new signatures are added to history when committed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCommit = (next: ESignValue) => {
    onChange(next);
    addESignHistory(next);
    setIsModalOpen(false);
  };

  const signedAt = formatSignatureTimestamp(value.timestamp);

  return (
    <>
      {isSigned ? (
        <div id={fieldKey} css={signedContainerStyle} aria-invalid={hasError} tabIndex={-1}>
          <ESignPreview value={value} fieldKey={fieldKey} />
          {signedAt && (
            <Text size='small' color='light' css={signedMetaStyle}>
              {t('Signed {{timestamp}}', { timestamp: signedAt })}
            </Text>
          )}
          <TextLink size='small' weight='bold' onClick={() => setIsModalOpen(true)}>
            {t('Update Signature')}
          </TextLink>
        </div>
      ) : (
        <NakedButton
          id={fieldKey}
          css={signBoxStyle}
          aria-invalid={hasError}
          aria-haspopup='dialog'
          onClick={() => setIsModalOpen(true)}
        >
          <Text as='span' size='medium' color='light'>
            {t('Click/Tap inside this box to sign')}
          </Text>
        </NakedButton>
      )}

      <ESignModal
        show={isModalOpen}
        fieldKey={fieldKey}
        onClose={() => setIsModalOpen(false)}
        onCommit={handleCommit}
      />
    </>
  );
};
